import { put, take, fork } from 'redux-saga/effects';
import { select, call } from 'typed-redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import {
  xcriticalFormError,
  xcriticalFormInit,
  xcriticalFormReset,
  xcriticalFormShowErrors,
} from '@xcritical/forms';

import { SSHelper } from '@crm-package/utils';

import {
  getChatState,
  chatActions,
  getCurrentChatSelector,
  getNewMessageForm,
  getSearchForm,
} from '../store';
import { NEW_MESSAGE_FORM_NAME, SEARCH_FORM_NAME } from '../consts';
import { IChat } from '../types';

import { getChats, getCurrentChatData, searchChat } from './api';
import { getSocket, handleChatError } from './helpers';
import { getToken } from './utils';
import { websocketInitChannel } from './websocket';

export function* handleInit() {
  try {
    const result = yield* call(getToken);

    if (result) {
      yield put(chatActions.enable());

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      yield fork(handleStartWebSocket, result);
    }
  } catch (e) {
    yield* handleChatError(e);
  }
}

function* handleStartWebSocket(token: string) {
  const socket = getSocket(token);
  const channel = yield* call(websocketInitChannel, socket);
  while (true) {
    const action = yield take(channel);
    yield put(action);
  }
}

export function* handleInitChats() {
  try {
    yield put(chatActions.setIsLoading(true));
    const token = yield* getToken();

    if (token) {
      const result = yield* call(getChats, token);
      yield put(chatActions.setChats(result.chats));
      yield put(chatActions.setLastMessageId(result.lastMessageId ?? 0));
    }
  } catch (e) {
    yield* handleChatError(e);
  } finally {
    yield put(chatActions.setIsLoading(false));
  }
}

export function* handleInitCreatedChat({ payload }: PayloadAction<IChat>) {
  try {
    const chat = yield* select(getCurrentChatSelector);

    if (chat && chat.uniqueKey === payload.uniqueKey) {
      yield put(chatActions.setActiveChat(payload));
    }
  } catch (e) {
    yield* handleChatError(e);
  }
}

export function* handleRefreshChat() {
  try {
    yield put(chatActions.setIsLoading(true));
    const tokenManager = yield* getToken();
    const chat = yield* select(getCurrentChatSelector);

    if (tokenManager && chat) {
      const { id, clientId } = chat;

      if (id && id > 0) {
        const token = yield* getToken(clientId);
        const socket = getSocket();

        if (token) {
          socket.emit('chat:refresh', { token, chatId: id });
        }
      }
    }
  } catch (e) {
    yield* handleChatError(e);
  } finally {
    yield put(chatActions.setIsLoading(false));
  }
}

export function* handleGetChatMessages({ payload }: PayloadAction<number>) {
  try {
    yield put(chatActions.setIsLoading(true));
    const chat = yield* select(getCurrentChatSelector);

    if (!chat || chat.id < 0) return;

    const token = yield* getToken(chat.clientId);

    if (token) {
      const result = yield* call(getCurrentChatData, token, payload);
      yield put(chatActions.setCurrentChats(result));
    }
  } catch (e) {
    yield* handleChatError(e);
  } finally {
    yield put(chatActions.setIsLoading(false));
  }
}

export function* handleSendMessage() {
  try {
    const { activeChatId, currentChat } = yield* select(getChatState);
    const token = yield* getToken(currentChat?.clientId);

    if (!token) return;

    const {
      model: { message },
    } = yield* select(getNewMessageForm);

    if (message && message.length > 4096) {
      yield put(xcriticalFormShowErrors(NEW_MESSAGE_FORM_NAME, true));
      yield put(
        xcriticalFormError(NEW_MESSAGE_FORM_NAME, {
          message: 'Message must be at most 4096 characters',
        })
      );

      return;
    }

    if (activeChatId !== undefined && message) {
      const { phoneIndex, channelId } = currentChat!;
      const socket = getSocket();
      socket.emit('message:send_new', {
        message,
        phoneIndex,
        channelId,
        token,
      });

      if (activeChatId <= 0) yield put(chatActions.setFoundedChats([]));
    }

    yield put(xcriticalFormReset(NEW_MESSAGE_FORM_NAME));
  } catch (e) {
    yield* handleChatError(e);
  }
}

export function* handleSearch({ payload }: PayloadAction<number | undefined>) {
  yield put(chatActions.setSearchIsLoading(true));

  try {
    const { isOpen, isEnabled } = yield* select(getChatState);

    if (isEnabled) {
      if (payload)
        yield put(xcriticalFormInit(SEARCH_FORM_NAME, { searchId: payload }));

      const {
        model: { searchId },
      } = yield* select(getSearchForm);

      const token = yield* getToken(searchId);

      if (!token) return;

      const result = yield call(searchChat, searchId, token);
      // const socket = getSocket();
      // socket.emit('search_chats', { searchId, token });

      if (!isOpen) {
        yield put(chatActions.open());
      }

      if (result.chats?.length)
        yield put(chatActions.setFoundedChats(result.chats));
    }
  } catch (e) {
    yield* handleChatError(e, true);
  } finally {
    yield put(chatActions.setSearchIsLoading(false));
  }
}

export function* handleLogout() {
  SSHelper.clear();
  yield put(chatActions.logout());
}

export function* handleGetIsLeadChatsEnable({
  payload,
}: PayloadAction<number>) {
  try {
    const { isEnabled } = yield* select(getChatState);

    if (isEnabled) {
      const token = yield* getToken(payload);

      if (!token) return;

      const result = yield* call(searchChat, payload, token);

      if (result.chats.length)
        yield put(chatActions.setAvailableLeads(payload));
    }
  } catch (e) {
    yield* handleChatError(e, true);
  }
}
